import * as _ from 'underscore';

import { LongRunningAction } from '~permissions/services/LongRunningAction';
import { ILongRunningAction } from '~permissions/services/types/ILongRunningAction';

import { requestSpaceContents } from '../../../../app/actions/spaces/RequestSpaceContentsActions';
import { getAppStore } from '../../../../app/AppStore';
import { Messages } from '../../../../codegen/Localize';
import { openReactActionDialog } from '../../../../commonInfra/dialogs/ReactActionDialog';
import { openReactConfirmationDialog } from '../../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../../../httpClient/utils/ResultHelper';
import { CommonErrorCode } from '../../../../ts/helpers/ErrorCodes';
import { L10n } from '../../../../ts/L10n/L10n';
import { IGroupBasicInfo } from '../../../../ts/models/IGroupInfo';
import { GrantLicenseModes, ISyncAdGroupCompleteResult } from '../../../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IActionResult } from '../../../../ts/ServerApi/types/IActionResult';
import { IGroup, LocalGroupDomainName } from '../../../../ts/ServerApi/types/IGroup';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { AddActiveDirectoryGroupDialogBody, AddActiveDirectoryGroupDialogBodyProps, AddActiveDirectoryGroupDialogState } from '../../../components/AddActiveDirectoryGroupDialog';
import { DefaultSiteRole } from '../../../services/resources/SiteRoleOptions';
import { ActiveDirectoryGroups } from '../ActiveDirectoryGroups';

type ADGroupAction = 'add'|'sync';

export function showSyncAdGroupDialog(group: IGroupBasicInfo): Promise<any>{
    const dialogBody = Messages.confirmSyncAdGroup({GROUP_NAME: L10n.elideIfLong(group.name)});
    const modalInstance = openReactConfirmationDialog<void, IResult>({
        title: Messages.synchronize(),
        message: dialogBody,
        actionName: Messages.synchronize(),
        action: () => {
            const responsePromise = ActiveDirectoryGroups.syncAdGroup(group.name, group.domainName);
            responsePromise
            .then((result: IResult) => {
                return ResultHelper.hasErrors(result) ? Promise.reject(result) : Promise.resolve(result);
            })
            .then((result: IActionResult) => {
                // notification
                ToasterService.instance.pop({
                    type: ToastType.Info,
                    text: Messages.syncAdGroupSubmitted({ GROUP_NAME: L10n.elideIfLong(group.name) })
                });
                return result;
            })
            .then(LongRunningAction.getActionIdFromActionResult)
            .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId))
            .then((longRunningAction: ILongRunningAction) => LongRunningAction.getActionResult<ISyncAdGroupCompleteResult>(longRunningAction))
            .then(handleSyncCompleteFor(group.name, group.domainName, 'sync'))
            .catch(handleSyncErrorResultFor(group.name, group.domainName, 'sync'));

            return responsePromise;
        },
        paramsForAction: undefined,
        dialogWidth: TabDialogWidth.width375px
    });

    return modalInstance.result;
}

function isActiveDirectory(group: IGroup): boolean {
  return group.domainName && group.domainName !== LocalGroupDomainName;
}

export function showSyncAdGroupsDialog(selectedGroups: IGroupBasicInfo[]): Promise<any>{
    const groups = _.filter(selectedGroups, isActiveDirectory);
    if(groups.length === 1) {
        // Only 1 active directory group was selected, use the single AD dialog
        return showSyncAdGroupDialog(groups[0]);
    } else {
        const modalInstance = openReactConfirmationDialog<void, IResult>({
            title: Messages.synchronize(),
            message: Messages.confirmSyncAdGroups({ GROUP_COUNT_LOC: L10n.localizedCount(groups.length)}),
            actionName: Messages.synchronizeConfirm({ COUNT: groups.length, COUNT_LOC: L10n.localizedCount(groups.length)}),
            paramsForAction: undefined,
            action: () => {
                const responsePromise = ActiveDirectoryGroups.syncAdGroups(groups);

                // Display results
                responsePromise
                .then((result: IResult) => {
                    return ResultHelper.hasErrors(result) ? Promise.reject(result) : Promise.resolve(result);
                })
                .then((result: IActionResult) => {
                    const successMessage = Messages.syncAdGroupsSubmitted({ GROUP_COUNT_LOC: L10n.localizedCount(groups.length)});
                    ToasterService.instance.pop({ type: ToastType.Info, text: successMessage });
                    return result;
                })
                .catch((result: IActionResult) => {
                    const errorMessage = Messages.syncAdGroupsError({ GROUP_COUNT_LOC: L10n.localizedCount(groups.length)});
                    ToasterService.instance.pop({ type: ToastType.Error, text: errorMessage });
                });

                return responsePromise;
            },
            dialogWidth: TabDialogWidth.width375px
        });
        return modalInstance.result;
    }
}

export function showAddAdGroupDialog(): Promise<any> {
    const modalInstance = openReactActionDialog<AddActiveDirectoryGroupDialogBodyProps, AddActiveDirectoryGroupDialogState, any>({
        title:  Messages.addUsersImportAdGroupTitle(),
        bodyComponent: AddActiveDirectoryGroupDialogBody,
        initialBodyProps: {
          prompt: Messages.addUsersImportAdGroupSelectGroupTitle()
        },
        initialDialogState: { groupModel: undefined, siteRole: DefaultSiteRole, grantLicenseMode: GrantLicenseModes.ON_SYNC },
        actionName: Messages.addUsersImportAdGroupSubmit(),
        dismissDialogOnActionFailed: true,
        action: (_bodyProps: AddActiveDirectoryGroupDialogBodyProps, dialogState: AddActiveDirectoryGroupDialogState) => {
          const group = dialogState.groupModel;
          return ActiveDirectoryGroups.syncAdGroup(group.name, group.domainName, dialogState.siteRole, dialogState.grantLicenseMode)
          .then((result: IResult) => {
              return ResultHelper.hasErrors(result) ? Promise.reject(result) : Promise.resolve(result);
            })
          .then((result: IActionResult) => {
            ToasterService.instance.pop({
              type: ToastType.Info,
              text: Messages.addAdGroupSubmitted({GROUP_NAME: L10n.elideIfLong(group.name)})
            });
            modalInstance.close();
            return result;
          })
          .then(LongRunningAction.getActionIdFromActionResult)
          .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId))
          .then((longRunningAction: ILongRunningAction) => LongRunningAction.getActionResult<ISyncAdGroupCompleteResult>(longRunningAction))
          .then((result) => {
            getAppStore().dispatch(requestSpaceContents());
            return handleSyncCompleteFor(group.name, group.domainName, 'add')(result);
          })
          .catch(handleSyncErrorResultFor(group.name, group.domainName, 'add'));
        },
        actionDisabled: (_bodyProps: AddActiveDirectoryGroupDialogBodyProps, dialogState: AddActiveDirectoryGroupDialogState) => dialogState.groupModel === undefined,
        dialogWidth: TabDialogWidth.width500px,
    });

    return modalInstance.result;
}

function handleSyncErrorResultFor(groupName: string, groupDomainName: string, actionType: ADGroupAction){
    return (result?: IResult) => {
        if(!result || !result.errors) return; // Must have failed for some reason with out an error code, like cancel

        const errorStringArgs = {
          GROUP_NAME: L10n.elideIfLong(groupName),
          DOMAIN_NAME: L10n.elideIfLong(groupDomainName)
        };
        let adGroupErrorText: string = actionType === 'add' ? Messages.addAdGroupError(errorStringArgs) : Messages.syncAdGroupError(errorStringArgs);
        if (result.errors[0].code === CommonErrorCode.AdGroupNotFound) {
          // ad group not found is the only thing we expect to handle at the moment
          ToasterService.instance.pop({
              type: ToastType.Error,
              text: Messages.errorActiveDirectoryGroupNotFound(errorStringArgs)
            });
        } else {
          ToasterService.instance.pop({
              type: ToastType.Error,
              text: adGroupErrorText
            });
        }

        return result; // For chaining
      };
}

function handleSyncCompleteFor(groupName: string, groupDomainName: string, actionType: ADGroupAction){
    return (result: ISyncAdGroupCompleteResult) => {

        // Create success messages

        const completeMessageStringArgs = {
            GROUP_NAME: L10n.elideIfLong(groupName),
            DOMAIN_NAME: L10n.elideIfLong(groupDomainName)
        };
        let adGroupSuccessText: string = actionType === 'add' ? Messages.addAdGroupSuccess(completeMessageStringArgs) : Messages.syncAdGroupSuccess(completeMessageStringArgs);
        const usersAddedSuccessCount = result.usersAddedToGroup || 0;
        const usersRemovedSuccessCount = result.usersRemovedFromGroup || 0;

        const successLines = [
            adGroupSuccessText,
            Messages.syncAdGroupSuccessSummary({
                NUM_ADDED: usersAddedSuccessCount,
                NUM_ADDED_LOC: L10n.localizedCount(usersAddedSuccessCount),
                NUM_REMOVED: usersRemovedSuccessCount,
                NUM_REMOVED_LOC: L10n.localizedCount(usersRemovedSuccessCount)
            })
        ];

        if(result.usersAddedToSite > 0) {
            successLines.push(
                Messages.syncAdGroupSuccessNumAddedToSite({
                    NUM_ADDED: result.usersAddedToSite,
                    NUM_ADDED_LOC: L10n.localizedCount(result.usersAddedToSite)
                })
            );
        }
        if(result.usersSiteRoleUpdated > 0) {
            successLines.push(
                Messages.syncAdGroupSuccessNumSiteRoleUpdated({
                    NUM_UPDATED: result.usersSiteRoleUpdated,
                    NUM_UPDATED_LOC: L10n.localizedCount(result.usersSiteRoleUpdated)
                })
            );
        }

        ToasterService.instance.pop({
            type: ToastType.Info,
            text: successLines.join('\n'),
            timeoutDelay: ToasterService.NoTimeout
        });

        // Create error messages
        let adGroupErrorText: string = actionType === 'add' ? Messages.addAdGroupError(completeMessageStringArgs) : Messages.syncAdGroupError(completeMessageStringArgs);
        const errorLines = [
            adGroupErrorText
        ];
        if(result.usersWithInsufficientLicenses > 0) {
            errorLines.push(
                Messages.syncAdGroupErrorInsufficientLicenses({
                    COUNT: result.usersWithInsufficientLicenses,
                    COUNT_LOC: L10n.localizedCount(result.usersWithInsufficientLicenses)
                })
            );
        }
        if(result.usersSkipped > 0) {
            errorLines.push(
               Messages.syncAdGroupErrorNumUsersSkipped({
                   COUNT: result.usersWithInsufficientLicenses,
                   COUNT_LOC: L10n.localizedCount(result.usersWithInsufficientLicenses)
                })
            );
        }
        if(errorLines.length > 1) { // Errors actually occurred if there is more than the default error message
            ToasterService.instance.pop({
                type: ToastType.Error,
                text: errorLines.join('\n')
            });
        }

        return result; // For Chaining
    };
}

export const ActiveDirectoryGroupAction = {
    showSyncAdGroupDialog,
    showSyncAdGroupsDialog,
    showAddAdGroupDialog
};

export type ActiveDirectoryGroupAction = typeof ActiveDirectoryGroupAction;
